/**
 * ChatMessage.tsx
 *
 * Refactored chat message component using extracted message type components.
 * Location: src/app/modules/chat/components/chat-detail/
 * Reduced from ~1159 lines to ~350 lines.
 */

import clsx from "clsx";
import firebase from "firebase/compat/app";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { shallowEqual, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";

// Resource imports (absolute paths)
import { MenuComponent } from "src/resources/assets/ts/components";
import "src/styles/css/color.css";
import "src/styles/css/dropdown.scss";
import { readIdFromReferredData } from "src/util/ReadReferredData";
import * as LOG from "src/util/SDayslogger";
import { RootState } from "src/setup/redux/store";

// Model imports
import Customer from "src/app/models/Customer";
import Message, { InteractiveMessageComponent } from "src/app/models/Message";
import User from "src/app/models/User";

// Legacy component imports (from chat-handle until fully migrated)
import ChatFailedMenu from "src/app/layout/chat-handle/components/chat-menu/ChatFailedMenu";
import ChatMessageMenu from "src/app/layout/chat-handle/components/chat-menu/ChatMessageMenu";
import ChatMessageReplyView from "src/app/layout/chat-handle/components/chat-replymessage/ChatMessageReplyView";
import { convertTextStyle } from "src/util/TextFormatter";
import ChatTime from "src/app/layout/chat-handle/components/chat-message/ChatTime";

// New extracted components (from chat module)
import {
  TextMessage,
  MediaMessage,
  DocumentMessage,
  AudioMessage,
  LocationMessage,
  OrderMessage,
  UnsupportedMessage,
} from "../chat-message";
import FailedProductCard from "../chat-message/FailedProductCard";
import { normalizeMessageType } from "../../types";

type MessageProps = {
  message: Message;
  index: number;
  customer?: Customer;
  user?: User;
  messages: Message[];
  collaborationId: string;
  companyID: string;
  debug: boolean;
  phoneNumber?: string;
  lastUserInteraction?: firebase.firestore.Timestamp;
};

const ChatMessage: FC<MessageProps> = (props) => {
  const { t } = useTranslation();
  const {
    message,
    lastUserInteraction,
    index,
    customer,
    user,
    messages,
    collaborationId,
    companyID,
    phoneNumber,
    debug,
  } = props;
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 900px)" });

  // Create allMediaInRoom array from messages
  // Supports both new (message.media.*) and legacy (message.mediaUrl) formats
  const allMediaInRoom =
    messages
      ?.filter(
        (msg) =>
          (msg.messageType?.toLowerCase() === "image" ||
            msg.messageType?.toLowerCase() === "image_with_text" ||
            msg.messageType?.toLowerCase() === "video" ||
            msg.messageType?.toLowerCase() === "sticker") &&
          (msg.media?.url || msg.mediaUrl)  // Support both formats
      )
      .map((msg) => ({
        id: msg.id,
        type:
          msg.messageType?.toLowerCase() === "video"
            ? ("video" as const)
            : msg.messageType?.toLowerCase() === "sticker"
            ? ("sticker" as const)
            : msg.messageType?.toLowerCase() === "image_with_text"
            ? ("image_with_text" as const)
            : ("image" as const),
        // Support both new and legacy formats for all fields:
        url: msg.media?.url || msg.mediaUrl!,
        name: msg.media?.filename || msg.filename || msg.media?.fileRecordId || msg.fileId || "Untitled",
        thumbnail: msg.media?.url || msg.mediaUrl!,
        // Include all metadata for proper download support:
        // Priority: filename > fileRecordId > undefined
        filename: msg.media?.filename || msg.filename,
        fileRecordId: msg.media?.fileRecordId || msg.fileId,
        mimeType: msg.media?.mimeType,
        extension: msg.media?.extension,
        duration: msg.media?.duration || msg.duration,
        size: msg.media?.size
      })) || [];

  const [previousMedia, setPreviousMedia] = useState<string>("");
  const [nextMedia, setNextMedia] = useState<string>("");
  const [replyMessageID, setReplyMessageID] = useState<string>("");

  const { activeTab } = useSelector(
    (state: RootState) => state.ChatV2,
    shallowEqual
  );
  const { collaborationsById, selectedCollaborationId } = useSelector(
    (state: RootState) =>
      state.ChatV2[activeTab] ?? {
        collaborationOrder: [],
        collaborationsById: {},
        hasMore: false,
        isLoading: false,
        selectedCollaborationId: null,
        selectedRoomId: null,
      },
    shallowEqual
  );

  // Initialize menu component
  useEffect(() => {
    setTimeout(() => {
      MenuComponent.reinitialization();
    }, 50);
  }, []);

  // Set up reply message ID and media navigation
  useEffect(() => {
    const findMessage = messages.find((obj) => obj.id === message.id);
    const replyId = readIdFromReferredData(findMessage?.replyMessageID);
    setReplyMessageID(findMessage?.replyMessageID?.id ?? replyId ?? "");

    // Build media navigation list
    const listMediaUrl: string[] = [];
    messages.forEach((obj) => {
      if (
        obj.messageType !== undefined &&
        (obj.messageType?.toLowerCase() === "video" ||
          obj.messageType?.toLowerCase() === "image")
      ) {
        listMediaUrl.push(
          `kt_modal_${obj.messageType?.toLowerCase()}_${obj.id}`
        );
      }
    });

    const currentMediaId = `kt_modal_${message?.messageType?.toLowerCase()}_${
      message.id
    }`;
    for (let i = 0; i < listMediaUrl.length; i++) {
      if (listMediaUrl[i] === currentMediaId) {
        setNextMedia(listMediaUrl[i + 1] || "");
        if (i > 0) {
          setPreviousMedia(listMediaUrl[i - 1]);
        }
      }
    }
  }, [messages, message.id, message.messageType]);

  // Don't render if message has no displayable content — EXCEPT for
  // outbound failed sends. We still want to surface those so the user
  // sees the bubble (and the retry affordance below) instead of the
  // message vanishing silently. Affects failed `interactive` / `other`
  // sends that arrived without enriched content.
  const isOutboundFailed =
    message.destination === "outbound" && message.status === "failed";
  if (
    message.textContent?.trim() === "" &&
    !message.mediaUrl &&
    !message.product &&
    !message.order &&
    !message.orderSN &&
    !message.location &&
    !isOutboundFailed
  ) {
    return null;
  }

  // Determine message direction and styling
  const msgtype = message.destination === "inbound" ? "in" : "out";
  let bgChat =
    msgtype !== "in"
      ? "cl-7days"
      : `cl-${message.channel.toString().toLowerCase()}`;
  const txChat = "black";

  const contentClass = `d-flex justify-content-${
    msgtype === "in" ? "start" : "end"
  } mb-3`;
  const contentType = `justify-content-${msgtype === "in" ? "start" : "end"}`;

  // Menu handlers
  const closeMenuHandler = (menuId: string) => {
    const menu = document.getElementById(menuId);
    if (menu) {
      menu.classList.add("hide-child");
      menu.click();
    }
  };

  const openMenuHandler = () => {
    const menu = document.getElementById(`message_menu_${message.id}`);
    if (menu) {
      menu.classList.remove("hide-child");
    }
  };

  // Render template messages (kept inline as they're complex)
  const renderTemplate = () => {
    const messageHeader: InteractiveMessageComponent | undefined = message
      ?.interactive?.components
      ? message.interactive.components.find((c) => c.type === "header")
      : undefined;

    const renderTemplateContent = () => (
      <div
        className={clsx({
          "mw-200px": isTabletOrMobile,
          "mw-lg-300px": !isTabletOrMobile,
        })}
      >
        <div
          style={{ color: txChat }}
          className={clsx(`p-5 rounded fw-bold ${bgChat}`)}
        >
          {message.header && <div className="fw-bolder">{message.header}</div>}
          {!message.header && messageHeader?.image && (
            <div className="d-flex">
              <img
                alt=""
                src={messageHeader.image}
                className="rounded"
                style={{ height: "auto", width: "100%" }}
              />
            </div>
          )}
          {!message.header && messageHeader?.video && (
            <div className="d-flex">
              <video
                controls
                src={messageHeader.video}
                className="rounded"
                style={{ height: "auto", width: "100%" }}
              />
            </div>
          )}
          {!message.header && messageHeader?.document && (
            <div className="d-flex flex-fill bg-secondary search-toolbar mw-100 h-auto align-items-start image-input">
              <div className="w-30 m-2 align-self-center">
                <i className="bi bi-file-earmark-text fs-3x text-dark" />
              </div>
              <div className="flex-grow-1 m-2 align-self-center break-word">
                {messageHeader?.filename || "Header Document"}
              </div>
              <a
                href={messageHeader.document}
                target="_blank"
                rel="noreferrer"
                className="d-block w-30 m-5 align-self-center"
              >
                <i className="bi bi-download fs-1 text-dark" />
              </a>
            </div>
          )}
          <div
            className="mt-2"
            dangerouslySetInnerHTML={{
              __html: convertTextStyle(message.textContent)
                .replaceAll("\\n", "<br/>")
                .replaceAll("\n", "<br/>"),
            }}
          />
          {message.footer && <div className="fw-lighter">{message.footer}</div>}
        </div>
        {message.buttons && (
          <div className="mt-1 d-grid gap-1">
            {message.buttons.map((button, i) => (
              <button
                key={i}
                type="button"
                style={{ outline: "none", border: "none" }}
                className={clsx(
                  `px-5 py-3 rounded fw-bolder ${bgChat} text-center text-primary`
                )}
              >
                {button}
              </button>
            ))}
          </div>
        )}
      </div>
    );

    if (message.replyMessageID) {
      return (
        <ChatMessageReplyView
          bgChat={bgChat}
          replyMessageID={replyMessageID}
          customer={customer}
          phoneNumber={phoneNumber}
        >
          {renderTemplateContent()}
        </ChatMessageReplyView>
      );
    }
    return renderTemplateContent();
  };

  // Render message content based on type
  const renderMessageContent = () => {
    const messageType = normalizeMessageType(message.messageType);
    console.log(
      `[DEBUG-MSG] renderMessageContent id=${message.id} raw=${
        message.messageType
      } normalized=${messageType} hasProduct=${!!message.product}`
    );
    const baseProps = { message, bgChat, txChat, isTabletOrMobile };
    const replyProps = { ...baseProps, replyMessageID, customer, phoneNumber };

    // Outbound failed sends — special-case rendering so the bubble
    // actually shows. Without this the message would either render
    // blank (TextMessage with no content) or fall through to
    // UnsupportedMessage.
    //
    // Branch on `product.marketplaceProductId` (set by the server
    // adapter's floor assignment even when detail enrichment fails)
    // rather than `messageType`, since the floor survives even when
    // the TikTok detail fetch threw before populating display fields.
    if (isOutboundFailed) {
      // Client `Message.product.marketplaceProductId` is typed `number`,
      // but TikTok product ids are 19-digit strings — stringify for the
      // card's mono display so we don't lose precision visually.
      const failedProductId = message.product?.marketplaceProductId;
      if (failedProductId !== undefined && failedProductId !== null) {
        return (
          <FailedProductCard
            productId={String(failedProductId)}
            errorCode={message.error?.errorCode}
            rawErrorMessage={message.error?.errorMessage}
          />
        );
      }

      // Non-product failed sends — keep the generic placeholder so the
      // user still sees the bubble and the adjacent retry control.
      if (
        message.textContent?.trim() === "" &&
        !message.mediaUrl &&
        !message.product?.displayName
      ) {
        return (
          <div
            style={{ color: txChat }}
            className={clsx(`p-3 rounded ${bgChat}`, {
              "mw-200px": isTabletOrMobile,
              "mw-lg-300px": !isTabletOrMobile,
            })}
            data-testid={`failed-message-fallback-${message.id}`}
          >
            <span className="fs-7 text-muted">
              {t("chat.failedMessage.placeholder", {
                defaultValue: "Message failed to send",
              })}
            </span>
          </div>
        );
      }
    }

    switch (messageType) {
      case "text":
      case "button":
      case "faq_liveagent":
      case "product":
        return <TextMessage {...replyProps} debug={debug} />;

      case "document":
        return <DocumentMessage {...replyProps} />;

      case "image":
      case "image_with_text":
      case "video":
      case "sticker":
        return (
          <MediaMessage
            {...replyProps}
            allMediaInRoom={allMediaInRoom}
            previousMedia={previousMedia}
            nextMedia={nextMedia}
          />
        );

      case "audio":
        return <AudioMessage {...replyProps} />;

      case "location":
        return <LocationMessage {...replyProps} />;

      case "template":
        return renderTemplate();

      case "order":
        return (
          <div
            className={clsx(`rounded ${bgChat} d-flex flex-column`, {
              "mw-200px": isTabletOrMobile,
              "mw-lg-300px": !isTabletOrMobile,
            })}
          >
            <OrderMessage message={message} bgChat={bgChat} txChat={txChat} />
            {message.textContent && message.textContent !== "" && (
              <div
                style={{ color: txChat }}
                className="p-2"
                dangerouslySetInnerHTML={{
                  __html: convertTextStyle(message.textContent),
                }}
              />
            )}
          </div>
        );

      case "unsupported":
      default:
        return <UnsupportedMessage {...replyProps} />;
    }
  };

  return (
    <div
      key={`message${index}`}
      className={clsx("d-flex", contentClass)}
      style={{ paddingLeft: "12px", paddingRight: "12px" }}
    >
      {/* Chat Message Container */}
      <div
        className="d-flex flex-column"
        style={{ position: "relative" }}
        onMouseEnter={openMenuHandler}
        onMouseLeave={() => closeMenuHandler(`message_menu_${message.id}`)}
      >
        {/* Message Menu (Info & Reply) */}
        <div
          className="hide-child"
          id={`message_menu_${message.id}`}
          style={{ zIndex: 99 }}
        >
          <div
            id={`message_menu_toggle_${message.id}`}
            className="message-menu fas fa-chevron-circle-down cursor-pointer fs-3"
            data-kt-menu-trigger="click"
            data-kt-menu-attach="parent"
            data-kt-menu-placement="top-end"
            data-kt-menu-flip="bottom"
            data-testid={`message-menu-toggle-${message.id}`}
            style={{ color: "white" }}
          />
          <ChatMessageMenu userMessage={message} destination={msgtype} />
        </div>

        {/* Message Bubble */}
        <div
          id={`message_${message.id}`}
          className={clsx("d-flex flex-row", contentType)}
          data-testid={`inbound-message-${message.id}}`}
        >
          {/* Retry Message Menu if Status: "failed" */}
          {msgtype !== "in" &&
            (message.status === "failed" ||
              message.status === "" ||
              message.status === undefined) && (
              <ChatFailedMenu
                failedMessage={message}
                failedCustomer={customer}
                failedUserLastInteraction={lastUserInteraction}
                failedUser={user}
                failedCollaborationId={collaborationId}
                companyID={companyID}
                roomsModel={
                  collaborationsById[selectedCollaborationId]?.roomsModel
                }
              />
            )}

          {/* Message Content */}
          {renderMessageContent()}
        </div>

        {/* Message Timestamp and Status */}
        <div
          className={clsx("d-flex", {
            "ms-3": msgtype !== "in",
            "justify-content-start": msgtype === "in",
            "justify-content-end": msgtype !== "in",
          })}
        >
          <span className="d-flex flex-row text-muted fs-7 mb-1">
            {msgtype !== "in" && message?.source !== "autoreply" && (
              <i
                className={clsx("bi", "bi bi-check2-all text-primary", {
                  "fs-3": !isTabletOrMobile,
                  "fs-1": isTabletOrMobile,
                  "bi-clock": message.status === "submitted",
                  "bi-check2": message.status === "sent",
                  "bi-check2-all": message.status === "delivered",
                  "bi-check2-all text-primary": message.status === "read",
                })}
              />
            )}
            {msgtype !== "in" && message?.source === "autoreply" && (
              <p className="text-primary m-0 p-0 px-2">autoreply</p>
            )}
            {ChatTime(message.createdAt.seconds)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;
